import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClientCard = ({ client, language, onEdit, onViewCases, onGenerateInvoice, onDelete }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'inactive':
        return 'bg-muted text-muted-foreground';
      case 'pending':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: { en: 'Active', fr: 'Actif', ar: 'نشط' },
      inactive: { en: 'Inactive', fr: 'Inactif', ar: 'غير نشط' },
      pending: { en: 'Pending', fr: 'En attente', ar: 'قيد الانتظار' }
    };
    return labels?.[status]?.[language] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (language === 'ar') {
      return date?.toLocaleDateString('ar-MA');
    }
    return date?.toLocaleDateString('fr-MA');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-sm hover:shadow-elevation-md transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-base md:text-lg">
            {client?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)}
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base md:text-lg">{client?.name}</h3>
            <p className="text-sm text-muted-foreground">{client?.clientId}</p>
          </div>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(client?.status)}`}>
          {getStatusLabel(client?.status)}
        </span>
      </div>
      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Mail" size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-foreground truncate">{client?.email}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Phone" size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-foreground">{client?.phone}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Briefcase" size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-foreground">
            {client?.activeCases} {language === 'ar' ? 'قضايا نشطة' : language === 'fr' ? 'dossiers actifs' : 'active cases'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Scale" size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-foreground">{client?.practiceArea?.[language]}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Clock" size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">
            {language === 'ar' ? 'آخر تفاعل: ' : language === 'fr' ? 'Dernière interaction: ' : 'Last interaction: '}
            {formatDate(client?.lastInteraction)}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(client)}
          iconName="Edit"
          iconPosition="left"
          iconSize={14}
          className="flex-1 min-w-[120px]"
        >
          {language === 'ar' ? 'تعديل' : language === 'fr' ? 'Modifier' : 'Edit'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewCases(client)}
          iconName="Briefcase"
          iconPosition="left"
          iconSize={14}
          className="flex-1 min-w-[120px]"
        >
          {language === 'ar' ? 'القضايا' : language === 'fr' ? 'Dossiers' : 'Cases'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onGenerateInvoice(client)}
          iconName="Receipt"
          iconPosition="left"
          iconSize={14}
          className="flex-1 min-w-[120px]"
        >
          {language === 'ar' ? 'فاتورة' : language === 'fr' ? 'Facture' : 'Invoice'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(client)}
          iconName="Trash2"
          iconPosition="left"
          iconSize={14}
          className="flex-1 min-w-[120px] text-error hover:text-error"
        >
          {language === 'ar' ? 'حذف' : language === 'fr' ? 'Supprimer' : 'Delete'}
        </Button>
      </div>
    </div>
  );
};

export default ClientCard;